"use client";

import React, { createContext, useContext, useCallback, useState } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import { v4 as generateUuid } from "uuid";
import { LOCALSTORAGE_TEMPLATE_CREATE_KEY } from "src/constants";
import { useLocalStorage } from "../hooks/useLocalStorage";

export const COPILOT_QUERY_PARAM = "copilot_prompt";
export const COPILOT_PROMPT = "Help finish setting up my Zap";

type TemplateContextType = {
  createTemplate: (templateKey: string) => void;
  creationScreen: string | null;
  error: string | null;
  setError: (error: string | null) => void;
  handleSignupRedirect: () => void;
  zapTemplateAttemptId: string | undefined;
  removeLocalStorageTemplateCreateKey: () => void;
  templateToCreate: string | null;
  localTemplateKey: string | null;
  setCreationScreen: (creationScreen: string | null) => void;
  setTemplateToCreate: (templateToCreate: string | null) => void;
};

const TemplateContext = createContext<TemplateContextType | undefined>(
  undefined,
);

type TemplateExecutionProviderProps = {
  children: React.ReactNode;
  shouldCreateAttemptId?: boolean;
};

export const TemplateExecutionProvider = ({
  children,
  shouldCreateAttemptId = false,
}: TemplateExecutionProviderProps) => {
  const currentAccountId = useCurrentAccountId();

  // used only for Zap Templates
  const [zapTemplateAttemptId, setZapTemplateAttemptId] = useState<string>();
  const [error, setError] = useState<string | null>(null);
  const [creationScreen, setCreationScreen] = useState<string | null>(null);
  const [templateToCreate, setTemplateToCreate] = useState<string | null>(null);

  const {
    storedValue: localTemplateKey,
    setValue: setLocalStorageTemplateCreateKey,
    removeValue: removeLocalStorageTemplateCreateKey,
  } = useLocalStorage<string>(LOCALSTORAGE_TEMPLATE_CREATE_KEY);

  const handleSignupRedirect = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("create", "true");

    return window.location.assign(
      "/sign-up" +
        `?next=${encodeURIComponent(`${url.pathname}?${url.searchParams.toString()}`)}`,
    );
  };

  const createTemplate = useCallback(
    (templateKey: string) => {
      setZapTemplateAttemptId(
        shouldCreateAttemptId ? generateUuid() : undefined,
      );
      setError(null);
      setCreationScreen(currentAccountId ? templateKey : null);
      setTemplateToCreate(templateKey);
      if (!currentAccountId) {
        // Set the template we want to create in localStorage so it will kick off
        // execution when they land back on this page.
        setLocalStorageTemplateCreateKey(templateKey);

        handleSignupRedirect();
      }
    },
    [currentAccountId, setLocalStorageTemplateCreateKey, shouldCreateAttemptId],
  );

  return (
    <TemplateContext.Provider
      value={{
        createTemplate,
        creationScreen,
        error,
        setError,
        handleSignupRedirect,
        zapTemplateAttemptId,
        removeLocalStorageTemplateCreateKey,
        templateToCreate,
        localTemplateKey,
        setCreationScreen,
        setTemplateToCreate,
      }}
    >
      {children}
    </TemplateContext.Provider>
  );
};

export const useTemplateExecutionContext = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error(
      "useTemplateExecutionContext must be used within a TemplateExecutionProvider",
    );
  }
  return context;
};
